import { useEffect, useState, useLayoutEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import {
    Plus,
    Search,
    Edit2,
    Trash2,
    LogOut,
    Database,
    ExternalLink,
    Star,
    MoreVertical,
    ImageOff,
    Tag
} from 'lucide-react';
import { DictionaryEntry } from '../../types';

export const AdminDashboard = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [entries, setEntries] = useState<DictionaryEntry[]>([]);
    const [loading, setLoading] = useState(true);

    // State derived from URL
    const searchQuery = searchParams.get('q') || '';
    const selectedLetter = searchParams.get('letter');
    const missingFilter = searchParams.get('missing') || 'all'; // 'all', 'no-image', 'no-tags'

    const navigate = useNavigate();
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        fetchEntries();
    }, []);

    // Scroll restoration
    useLayoutEffect(() => {
        if (!loading && entries.length > 0 && scrollContainerRef.current) {
            const savedScroll = sessionStorage.getItem('admin_scroll_pos');
            if (savedScroll) {
                scrollContainerRef.current.scrollTop = parseInt(savedScroll, 10);
            }
        }
    }, [loading, entries.length]);

    const fetchEntries = async () => {
        setLoading(true);
        const { data, error } = await supabase
            .from('dictionary_entries')
            .select('*')
            .order('term', { ascending: true });

        if (error) {
            console.error('Error fetching entries:', error);
        } else {
            // Map Supabase snake_case to our camelCase types if necessary
            const mappedData = (data || []).map(item => ({
                ...item,
                fullDefinition: item.full_definition,
                image: item.image_url,
                isFeatured: item.is_featured
            }));
            setEntries(mappedData);
        }
        setLoading(false);
    };

    const toggleFeatured = async (entry: DictionaryEntry) => {
        const { error } = await supabase
            .from('dictionary_entries')
            .update({ is_featured: !entry.isFeatured })
            .eq('id', entry.id);

        if (error) {
            alert('Erreur lors de la mise à jour : ' + error.message);
        } else {
            fetchEntries();
        }
    };

    const handleDelete = async (id: number | string) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer cette définition ?')) {
            const { error } = await supabase
                .from('dictionary_entries')
                .delete()
                .eq('id', id);

            if (error) {
                alert('Erreur lors de la suppression : ' + error.message);
            } else {
                fetchEntries();
            }
        }
    };

    const handleLogout = async () => {
        await supabase.auth.signOut();
        navigate('/admin/login');
    };

    // Helpers to update URL state
    const updateSearchQuery = (val: string) => {
        setSearchParams(prev => {
            if (val) prev.set('q', val);
            else prev.delete('q');
            return prev;
        }, { replace: true });
    };

    const updateSelectedLetter = (val: string | null) => {
        setSearchParams(prev => {
            if (val) prev.set('letter', val);
            else prev.delete('letter');
            return prev;
        });
    };

    const updateMissingFilter = (val: string) => {
        setSearchParams(prev => {
            if (val && val !== 'all') prev.set('missing', val);
            else prev.delete('missing');
            return prev;
        });
    };

    // Save scroll position before navigating
    const handleEditNavigation = (id: number | string) => {
        if (scrollContainerRef.current) {
            sessionStorage.setItem('admin_scroll_pos', scrollContainerRef.current.scrollTop.toString());
        }
        navigate(`/admin/edit/${id}`);
    };

    // Calcul des statistiques par lettre
    const letterCounts = entries.reduce((acc, entry) => {
        const letter = (entry.letter || '').toUpperCase();
        if (letter) {
            acc[letter] = (acc[letter] || 0) + 1;
        }
        return acc;
    }, {} as Record<string, number>);

    const alphabets = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

    const filteredEntries = entries.filter(entry => {
        const matchesSearch = entry.term.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesLetter = !selectedLetter || entry.letter.toUpperCase() === selectedLetter;

        let matchesMissing = true;
        if (missingFilter === 'no-image') {
            matchesMissing = !entry.image || entry.image.trim() === '';
        } else if (missingFilter === 'no-tags') {
            matchesMissing = !entry.tags || entry.tags.length === 0;
        }

        return matchesSearch && matchesLetter && matchesMissing;
    });

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col">
            {/* Header */}
            <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
                <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center text-white">
                            <Database size={18} />
                        </div>
                        <h1 className="font-bold text-slate-900 hidden sm:block">Dico Admin</h1>
                    </div>

                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate('/')}
                            className="text-slate-500 hover:text-slate-900 text-sm font-medium flex items-center gap-1 px-3 py-2 rounded-lg hover:bg-slate-100 transition-colors"
                        >
                            Voir le site <ExternalLink size={14} />
                        </button>
                        <div className="h-6 w-px bg-slate-200 mx-1" />
                        <button
                            onClick={handleLogout}
                            className="text-red-500 hover:text-red-600 text-sm font-medium flex items-center gap-1 px-3 py-2 rounded-lg hover:bg-red-50 transition-colors"
                        >
                            Déconnexion <LogOut size={14} />
                        </button>
                    </div>
                </div>
            </header>

            <div className="flex-1 flex overflow-hidden">
                {/* Sidebar Navigation */}
                <aside className="w-64 bg-white border-r border-slate-200 flex flex-col hidden lg:flex">
                    <div className="p-4 border-b border-slate-100">
                        <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">Navigation par lettre</h2>
                        <button
                            onClick={() => updateSelectedLetter(null)}
                            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${!selectedLetter ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-50'}`}
                        >
                            <span>Toutes les lettres</span>
                            <span className="bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full text-xs">{entries.length}</span>
                        </button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 space-y-1">
                        {alphabets.map(letter => {
                            const count = letterCounts[letter] || 0;
                            return (
                                <button
                                    key={letter}
                                    disabled={count === 0}
                                    onClick={() => updateSelectedLetter(letter === selectedLetter ? null : letter)}
                                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${selectedLetter === letter ? 'bg-indigo-50 text-indigo-700' : count === 0 ? 'opacity-30 cursor-not-allowed' : 'text-slate-600 hover:bg-slate-50'}`}
                                >
                                    <span>{letter}</span>
                                    <span className={`px-2 py-0.5 rounded-full text-xs ${selectedLetter === letter ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-500'}`}>
                                        {count}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </aside>

                <main ref={scrollContainerRef} className="flex-1 overflow-y-auto px-4 py-8">
                    {/* Actions Bar */}
                    <div className="max-w-6xl mx-auto space-y-8">
                        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                            <div className="flex flex-col sm:flex-row gap-4 items-center flex-1 w-full">
                                <div className="relative w-full sm:w-64">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                                        <Search size={18} />
                                    </div>
                                    <input
                                        type="text"
                                        placeholder="Rechercher..."
                                        className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all shadow-sm"
                                        value={searchQuery}
                                        onChange={(e) => updateSearchQuery(e.target.value)}
                                    />
                                </div>

                                <div className="flex items-center gap-2 w-full sm:w-auto overflow-x-auto">
                                    <button
                                        onClick={() => updateMissingFilter('all')}
                                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors border flex items-center gap-2 ${missingFilter === 'all' ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                                    >
                                        Tous
                                    </button>
                                    <button
                                        onClick={() => updateMissingFilter('no-image')}
                                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors border flex items-center gap-2 ${missingFilter === 'no-image' ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                                        title="Sans image"
                                    >
                                        <ImageOff size={16} />
                                        <span className="hidden xl:inline">Sans image</span>
                                    </button>
                                    <button
                                        onClick={() => updateMissingFilter('no-tags')}
                                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors border flex items-center gap-2 ${missingFilter === 'no-tags' ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                                        title="Sans tags"
                                    >
                                        <Tag size={16} />
                                        <span className="hidden xl:inline">Sans tags</span>
                                    </button>
                                </div>

                                <div className="text-sm text-slate-500 font-medium hidden xl:block">
                                    {filteredEntries.length} résultats
                                </div>
                            </div>

                            <button
                                onClick={() => navigate('/admin/new')}
                                className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-6 rounded-xl shadow-lg shadow-indigo-100 flex items-center justify-center gap-2 transition-all active:scale-95 whitespace-nowrap"
                            >
                                <Plus size={18} /> Ajouter
                            </button>
                        </div>

                        {/* Mobile Letter Nav (visible on small screens) */}
                        <div className="lg:hidden flex overflow-x-auto gap-2 pb-2 scrollbar-none">
                            <button
                                onClick={() => updateSelectedLetter(null)}
                                className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-bold transition-colors ${!selectedLetter ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 border border-slate-200'}`}
                            >
                                TOUT ({entries.length})
                            </button>
                            {alphabets.map(letter => {
                                const count = letterCounts[letter] || 0;
                                if (count === 0) return null;
                                return (
                                    <button
                                        key={letter}
                                        onClick={() => updateSelectedLetter(letter === selectedLetter ? null : letter)}
                                        className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-bold transition-colors ${selectedLetter === letter ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 border border-slate-200'}`}
                                    >
                                        {letter} ({count})
                                    </button>
                                );
                            })}
                        </div>

                        {/* Table Content */}
                        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="bg-slate-50 border-b border-slate-200">
                                            <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Terme</th>
                                            <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider text-center">Vedette</th>
                                            <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Type</th>
                                            <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Tags</th>
                                            <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 text-sm">
                                        {loading ? (
                                            Array(5).fill(0).map((_, i) => (
                                                <tr key={i} className="animate-pulse">
                                                    <td className="px-6 py-4"><div className="h-4 bg-slate-100 rounded w-24"></div></td>
                                                    <td className="px-6 py-4 hidden md:table-cell"><div className="h-4 bg-slate-100 rounded w-16"></div></td>
                                                    <td className="px-6 py-4 hidden lg:table-cell"><div className="flex gap-2"><div className="h-4 bg-slate-100 rounded w-12"></div><div className="h-4 bg-slate-100 rounded w-12"></div></div></td>
                                                    <td className="px-6 py-4"></td>
                                                </tr>
                                            ))
                                        ) : filteredEntries.length === 0 ? (
                                            <tr>
                                                <td colSpan={4} className="px-6 py-12 text-center text-slate-500">
                                                    Aucune définition trouvée pour "{searchQuery}"
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredEntries.map((entry) => (
                                                <tr key={entry.id} className="hover:bg-slate-50 transition-colors group">
                                                    <td className="px-6 py-4">
                                                        <div className="font-semibold text-slate-900">{entry.term}</div>
                                                        <div className="text-slate-400 text-xs mt-0.5 truncate max-w-xs">{entry.definition}</div>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <button
                                                            onClick={() => toggleFeatured(entry)}
                                                            className={`p-2 rounded-lg transition-colors ${entry.isFeatured ? 'text-amber-500 bg-amber-50' : 'text-slate-300 hover:text-slate-400 hover:bg-slate-100'}`}
                                                            title={entry.isFeatured ? "Retirer des vedettes" : "Mettre en vedette"}
                                                        >
                                                            <Star size={18} className={entry.isFeatured ? 'fill-amber-500' : ''} />
                                                        </button>
                                                    </td>
                                                    <td className="px-6 py-4 hidden md:table-cell text-slate-600">
                                                        {entry.type}
                                                    </td>
                                                    <td className="px-6 py-4 hidden lg:table-cell">
                                                        <div className="flex flex-wrap gap-1">
                                                            {entry.tags.slice(0, 3).map(tag => (
                                                                <span key={tag} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs capitalize">
                                                                    {tag}
                                                                </span>
                                                            ))}
                                                            {entry.tags.length > 3 && <span className="text-xs text-slate-400">+{entry.tags.length - 3}</span>}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        <div className="flex items-center justify-end gap-2">
                                                            <button
                                                                onClick={() => handleEditNavigation(entry.id)}
                                                                className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-slate-100 rounded-lg transition-colors"
                                                            >
                                                                <Edit2 size={16} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDelete(entry.id)}
                                                                className="p-2 text-slate-400 hover:text-red-600 hover:bg-slate-100 rounded-lg transition-colors"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                            <button className="p-2 text-slate-400 hover:text-slate-900 md:hidden">
                                                                <MoreVertical size={16} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};
